import type { AcceleronTaxItems, GenericObject, paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { TaxClasses } from '@makemydeal/dr-platform-types';

type TaxItem = paymentServicesTypes.TaxItem;
type TaxItems = paymentServicesTypes.TaxItems;

export const applyManualTaxes = (manualTaxItems: TaxItems, taxItems: TaxItems): TaxItems => {
    const manualTaxRecords = manualTaxItems.reduce((records, item) => ({ ...records, [item.taxName]: item }), {});

    return taxItems.map((taxItem) => {
        const key = taxItem.taxName;
        if (key in manualTaxRecords) return manualTaxRecords[key as keyof typeof manualTaxRecords];
        return taxItem;
    });
};

export const isFlatTax = (taxItem: TaxItem) => {
    const taxItemProperties = ['class', 'taxClass', 'taxName'];
    return taxItemProperties.some((prop) => taxItem?.[prop as keyof typeof taxItem] === TaxClasses.FLAT);
};

export const mergeTaxItems = (
    currentTaxItems: AcceleronTaxItems,
    updatedTaxItems: AcceleronTaxItems,
    isManagerView: boolean,
    isSalesView: boolean = false,
    enableOverrideTaxFixes: boolean = false
): AcceleronTaxItems => {
    // DE720579 (gated by overrideTaxFixes_US1984109): SV must also preserve manual tax flags across
    // PAYMENT_RECEIVED merges, otherwise APPLY_DEALER_PAYMENT_OVERRIDES strips isManualTax and
    // subsequent PO calls recompute defaults, wiping the user's manual override.
    const currentTaxesMap = currentTaxItems
        .filter((taxItems) => isManagerView || (enableOverrideTaxFixes && isSalesView) || !taxItems.isManualTaxAmount)
        .reduce((records, item) => {
            if (isFlatTax(item)) return records;
            return { ...records, [item.taxName]: item };
        }, {} as GenericObject);

    return updatedTaxItems.map((taxItem) => {
        const isManualFlat = isFlatTax(taxItem) && !!taxItem.taxName;
        const currentTaxItem = currentTaxesMap[taxItem.taxName as keyof typeof currentTaxesMap];
        return {
            ...taxItem,
            isManualTax: currentTaxItem?.isManualTax || isManualFlat || false,
            manualChanges: currentTaxItem?.manualChanges,
            isManualTaxRate: currentTaxItem?.isManualTaxRate,
            isManualTaxAmount: currentTaxItem?.isManualTaxAmount
        };
    });
};

export const formatRate = (rate: number): number => {
    const numericRate = Number(rate);

    const adjustedRate = numericRate < 0.15 ? numericRate * 100 : numericRate;

    return parseFloat(adjustedRate.toFixed(5));
};

export const adjustTaxItemRates = (taxItems: TaxItems, enablePercentTaxRates: boolean = false): TaxItems => {
    return taxItems.map((taxItem) => {
        const adjustedTaxItem = structuredClone(taxItem);

        if (adjustedTaxItem.taxRate && !enablePercentTaxRates) {
            if (adjustedTaxItem.manualChanges?.taxRate) {
                adjustedTaxItem.taxRate = parseFloat((adjustedTaxItem.taxRate * 100).toFixed(5));
            } else {
                adjustedTaxItem.taxRate = formatRate(adjustedTaxItem.taxRate);
            }
        }

        return adjustedTaxItem;
    });
};
